import { useEffect, useRef, useState } from 'react';
import { useOnboardingStore } from '@/stores/onboarding.store';

const STEPS = [
  {
    title: 'Welcome to GoodChat!',
    description: "Let's show you around. This quick tour will help you get started.",
    selector: null, // modal center, no highlight
  },
  {
    title: 'Rooms',
    description: 'Rooms are where your team conversations happen. Join existing ones or create your own.',
    selector: '[data-tour="areas"]',
  },
  {
    title: 'Send a message',
    description: 'Type here to send your first message to the team.',
    selector: '[data-tour="message-input"]',
  },
  {
    title: 'Threads',
    description: 'Threads are named topics you can resolve — start one from the composer to keep a discussion organized.',
    selector: '[data-tour="threads"]',
  },
  {
    title: 'Inbox',
    description: 'Your inbox collects everything that needs your attention — unread messages, acknowledgments, and flagged items.',
    selector: '[data-tour="inbox"]',
  },
];

export function OnboardingTour() {
  const { tourActive, currentStep, nextStep, prevStep, skipTour, completeTour } = useOnboardingStore();
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!tourActive) return;

    const step = STEPS[currentStep];
    if (!step.selector) {
      setTargetRect(null);
      return;
    }

    const el = document.querySelector(step.selector);
    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect(rect);
    } else {
      setTargetRect(null);
    }
  }, [tourActive, currentStep]);

  if (!tourActive) return null;

  const step = STEPS[currentStep];
  const isLast = currentStep === STEPS.length - 1;
  const isFirst = currentStep === 0;

  // Calculate tooltip position
  let tooltipStyle: React.CSSProperties = {};
  if (targetRect) {
    const padding = 12;
    tooltipStyle = {
      position: 'fixed',
      top: targetRect.bottom + padding,
      left: Math.max(16, Math.min(targetRect.left, window.innerWidth - 340)),
      zIndex: 10002,
    };
    // If tooltip would go off bottom, place above
    if (targetRect.bottom + 200 > window.innerHeight) {
      tooltipStyle.top = targetRect.top - 200 - padding;
    }
  } else {
    tooltipStyle = {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 10002,
    };
  }

  return (
    <>
      {/* Overlay */}
      <div
        ref={overlayRef}
        className="fixed inset-0 z-[10000]"
        style={{ pointerEvents: 'none' }}
      >
        {/* Dark overlay with spotlight cutout */}
        <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'auto' }}>
          <defs>
            <mask id="spotlight-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {targetRect && (
                <rect
                  x={targetRect.left - 4}
                  y={targetRect.top - 4}
                  width={targetRect.width + 8}
                  height={targetRect.height + 8}
                  rx="8"
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect
            x="0" y="0" width="100%" height="100%"
            fill="rgba(0,0,0,0.5)"
            mask="url(#spotlight-mask)"
            onClick={skipTour}
          />
        </svg>
      </div>

      {/* Tooltip */}
      <div
        style={tooltipStyle}
        className="w-80 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-4"
      >
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
          {step.title}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          {step.description}
        </p>

        {/* Progress dots */}
        <div className="flex items-center justify-between">
          <div className="flex gap-1.5">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full transition-colors ${
                  i === currentStep ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={skipTour}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              Skip
            </button>
            {!isFirst && (
              <button
                onClick={prevStep}
                className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
              >
                Back
              </button>
            )}
            <button
              onClick={isLast ? completeTour : nextStep}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {isLast ? 'Done' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
